/**
 * 钱包余额卡片组件
 * 显示用户余额信息和基本操作
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import {
  selectWalletInfo,
  selectWalletLoading,
  selectWalletError,
  fetchWalletInfo,
} from '../store/walletSlice';
import { WalletApiService } from '../services/walletApi';

interface WalletBalanceCardProps {
  onRechargePress?: () => void;
  onRecordsPress?: () => void;
}

export const WalletBalanceCard: React.FC<WalletBalanceCardProps> = ({
  onRechargePress,
  onRecordsPress,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const walletInfo = useSelector((state: RootState) => selectWalletInfo(state));
  const loading = useSelector((state: RootState) => selectWalletLoading(state));
  const error = useSelector((state: RootState) => selectWalletError(state));

  React.useEffect(() => {
    if (!walletInfo) {
      dispatch(fetchWalletInfo());
    }
  }, [dispatch, walletInfo]);

  const handleRefresh = () => {
    dispatch(fetchWalletInfo());
  };

  if (loading && !walletInfo) {
    return (
      <View style={styles.card}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007AFF" />
          <Text style={styles.loadingText}>加载中...</Text>
        </View>
      </View>
    );
  }

  if (error && !walletInfo) {
    return (
      <View style={styles.card}>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>加载失败</Text>
          <Text style={styles.errorDescription}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={handleRefresh}>
            <Text style={styles.retryButtonText}>重试</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      {/* 余额显示 */}
      <View style={styles.balanceSection}>
        <Text style={styles.balanceLabel}>当前余额</Text>
        <View style={styles.balanceRow}>
          <Text style={styles.balanceAmount}>
            {walletInfo ? WalletApiService.formatAmount(walletInfo.balance) : '¥0.00'}
          </Text>
          {loading && (
            <ActivityIndicator
              size="small"
              color="#007AFF"
              style={styles.refreshIndicator}
            />
          )}
        </View>
      </View>

      {/* 统计信息 */}
      {walletInfo && (
        <View style={styles.statsSection}>
          <View style={styles.statItem}>
            <Text style={styles.statValue}>
              {WalletApiService.formatAmount(walletInfo.total_recharged)}
            </Text>
            <Text style={styles.statLabel}>累计充值</Text>
          </View>
          <View style={styles.statDivider} />
          <View style={styles.statItem}>
            <Text style={styles.statValue}>
              {WalletApiService.formatAmount(walletInfo.total_spent)}
            </Text>
            <Text style={styles.statLabel}>累计消费</Text>
          </View>
        </View>
      )}

      {/* 操作按钮 */}
      <View style={styles.actionSection}>
        <TouchableOpacity
          style={[styles.actionButton, styles.rechargeButton]}
          onPress={onRechargePress}
        >
          <Text style={styles.rechargeButtonText}>充值</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.actionButton, styles.recordsButton]}
          onPress={onRecordsPress}
        >
          <Text style={styles.recordsButtonText}>记录</Text>
        </TouchableOpacity>
      </View>

      {/* 刷新按钮 */}
      <TouchableOpacity
        style={styles.refreshButton}
        onPress={handleRefresh}
        disabled={loading}
      >
        <Text style={styles.refreshButtonText}>
          {loading ? '刷新中...' : '刷新'}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    margin: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },

  // 加载状态
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  loadingText: {
    fontSize: 16,
    color: '#666',
    marginTop: 8,
  },

  // 错误状态
  errorContainer: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  errorText: {
    fontSize: 18,
    color: '#FF3B30',
    fontWeight: 'bold',
    marginBottom: 4,
  },
  errorDescription: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 16,
  },
  retryButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 6,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },

  // 余额显示
  balanceSection: {
    alignItems: 'center',
    marginBottom: 20,
  },
  balanceLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 4,
  },
  balanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  balanceAmount: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#000',
  },
  refreshIndicator: {
    marginLeft: 8,
  },

  // 统计信息
  statsSection: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    marginBottom: 20,
    paddingVertical: 12,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000',
    marginBottom: 2,
  },
  statLabel: {
    fontSize: 12,
    color: '#666',
  },
  statDivider: {
    width: 1,
    height: 30,
    backgroundColor: '#E0E0E0',
  },

  // 操作按钮
  actionSection: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  actionButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  rechargeButton: {
    backgroundColor: '#007AFF',
    marginRight: 8,
  },
  recordsButton: {
    backgroundColor: '#F8F9FA',
    borderWidth: 1,
    borderColor: '#E0E0E0',
    marginLeft: 8,
  },
  rechargeButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  recordsButtonText: {
    color: '#007AFF',
    fontSize: 16,
    fontWeight: '600',
  },

  // 刷新按钮
  refreshButton: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  refreshButtonText: {
    color: '#007AFF',
    fontSize: 14,
  },
});
